import { finishRequestTiming, startRequestTiming } from "../performance/requestTiming";

export const API_BASE_URL = (process.env.REACT_APP_ANINA_AUTH_API_URL || "http://localhost:8000").replace(/\/$/, "");

const CACHE_ENABLED = process.env.NODE_ENV !== "test";
const SNAPSHOT_CACHE_TTL_MS = 5000;
const BARS_CACHE_TTL_MS = 15000;
const LIVE_DEFAULT_BAR_WARM_LIMIT = 1152;
const responseCache = new Map();
const inFlightRequests = new Map();
const liveBundleInFlightRequests = new Map();
const liveBundleInitializedSymbols = new Set();
let serverConfiguredFeed = "";

function cachedResponse(path, ttlMs) {
  if (!CACHE_ENABLED || !(ttlMs > 0)) return null;
  const entry = responseCache.get(path);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > ttlMs) {
    responseCache.delete(path);
    return null;
  }
  return entry.payload;
}

function storeResponse(path, payload, ttlMs) {
  if (!CACHE_ENABLED || !(ttlMs > 0)) return;
  responseCache.set(path, { payload, cachedAt: Date.now() });
}

function rememberServerConfiguredFeed(payload) {
  const feed = String(payload?.source?.feed || "").trim().toLowerCase();
  if (feed) serverConfiguredFeed = feed;
}

function selectedLiveSymbol() {
  if (!CACHE_ENABLED || typeof window === "undefined") return "";
  try {
    return String(window.localStorage.getItem("aninaSelectedSymbol") || "PLUG")
      .trim()
      .toUpperCase();
  } catch (error) {
    return "PLUG";
  }
}

function normalizedTestEnvironment() {
  return process.env.NODE_ENV === "test";
}

function currentNewYorkSession(now = new Date(Date.now())) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const hour = Number(values.hour);
    const minute = Number(values.minute);
   if (!Number.isFinite(hour) || !Number.isFinite(minute)) return "unknown";

const minutes = hour * 60 + minute;
const weekday = values.weekday;

if (weekday === "Sat") return "closed";
if (weekday === "Sun") {
  return minutes >= 20 * 60 ? "overnight" : "closed";
}

if (minutes < 4 * 60) return "overnight";
if (minutes >= 4 * 60 && minutes < 9 * 60 + 30) return "premarket";
if (minutes >= 9 * 60 + 30 && minutes < 16 * 60) return "regular";
if (minutes >= 16 * 60 && minutes < 20 * 60) return "after-hours";
if (minutes >= 20 * 60 && weekday !== "Fri") return "overnight";

return "closed";
  } catch (error) {
    return "unknown";
  }
}

function defaultLiveChartBarsFeed() {
  const session = currentNewYorkSession();

  if (session === "overnight") {
    return "boats";
  }

  if (session === "premarket" || session === "after-hours") {
    return "delayed_sip";
  }

  return "";
}

function normalizeBatchSymbols(symbols) {
  const seen = new Set();
  const normalized = [];
  for (const symbol of Array.isArray(symbols) ? symbols : []) {
    const value = String(symbol || "").trim().toUpperCase();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    normalized.push(value);
  }
  return normalized;
}

function stockBarsPath(symbol, timeframe, feed, limit, options = {}) {
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();
  const normalizedTimeframe = String(timeframe || "5m").trim();
  const normalizedFeed = feed ? String(feed).trim().toLowerCase() : "";
  const marketDataBasePath = options.publicMarketData === true ? "/public/market-data" : "/market-data";
  const effectiveFeed =
    normalizedFeed && normalizedFeed !== serverConfiguredFeed ? normalizedFeed : "";
  const params = new URLSearchParams({
    timeframe: normalizedTimeframe,
    limit: String(limit),
  });
  if (effectiveFeed) params.set("feed", effectiveFeed);
  return `${marketDataBasePath}/stocks/${encodeURIComponent(normalizedSymbol)}/bars?${params.toString()}`;
}

function warmSelectedSymbolBars(symbol) {
  if (!CACHE_ENABLED || normalizedTestEnvironment()) return;
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();
  if (!normalizedSymbol || normalizedSymbol !== selectedLiveSymbol()) return;

  getStockBars(normalizedSymbol, "5m", undefined, LIVE_DEFAULT_BAR_WARM_LIMIT).catch(
    () => {
      // Warm-up is opportunistic. The chart performs its normal authenticated
      // request and error handling if this background request cannot complete.
    },
  );
}

async function fetchMarketData(path, signal) {
  const timing = startRequestTiming("market-data", path);

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
      ...(signal ? { signal } : {}),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok) {
      const detail = payload?.detail || "Market data is unavailable.";
      const requestError = new Error(detail);
      requestError.status = response.status;
      throw requestError;
    }

    finishRequestTiming(timing, "ok");
    return payload;
  } catch (error) {
    finishRequestTiming(timing, error?.name === "AbortError" ? "aborted" : "error");
    throw error;
  }
}

async function readMarketData(path, options = {}) {
  const cacheMode = options.cacheMode || "default";
  const cacheTtlMs = Number(options.cacheTtlMs) || 0;
  const shouldUseCache = cacheMode !== "reload";

  if (shouldUseCache) {
    const cached = cachedResponse(path, cacheTtlMs);
    if (cached !== null) return cached;
  }

  const canShareInFlight = CACHE_ENABLED && shouldUseCache && !options.signal;
  if (canShareInFlight && inFlightRequests.has(path)) {
    return inFlightRequests.get(path);
  }

  const request = fetchMarketData(path, options.signal).then((payload) => {
    storeResponse(path, payload, cacheTtlMs);
    return payload;
  });

  if (canShareInFlight) inFlightRequests.set(path, request);

  try {
    return await request;
  } finally {
    if (canShareInFlight && inFlightRequests.get(path) === request) {
      inFlightRequests.delete(path);
    }
  }
}

export function clearMarketDataCache() {
  responseCache.clear();
  inFlightRequests.clear();
  liveBundleInFlightRequests.clear();
  liveBundleInitializedSymbols.clear();
  serverConfiguredFeed = "";
}

export function getStockLiveBundle(
  symbol,
  timeframe = "5m",
  feed,
  limit = LIVE_DEFAULT_BAR_WARM_LIMIT,
  options = {},
) {
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();
  const normalizedTimeframe = String(timeframe || "5m").trim();
  const normalizedFeed = feed ? String(feed).trim().toLowerCase() : "";
  const requestedBarsFeed = Object.prototype.hasOwnProperty.call(options, "barsFeed")
    ? options.barsFeed
    : defaultLiveChartBarsFeed();
  const normalizedBarsFeed = requestedBarsFeed
    ? String(requestedBarsFeed).trim().toLowerCase()
    : "";
  const includeDelayedClose = options.includeDelayedClose !== false;
  const params = new URLSearchParams({
    timeframe: normalizedTimeframe,
    limit: String(limit),
    include_delayed_close: String(includeDelayedClose),
  });
  if (normalizedFeed) params.set("feed", normalizedFeed);
  if (normalizedBarsFeed) params.set("bars_feed", normalizedBarsFeed);

  const request = readMarketData(
    `/market-data/stocks/${encodeURIComponent(normalizedSymbol)}/live-bundle?${params.toString()}`,
    {
      ...options,
      cacheTtlMs: options.cacheTtlMs ?? SNAPSHOT_CACHE_TTL_MS,
    },
  ).then((payload) => {
    const snapshot = payload?.snapshot;
    if (!normalizedFeed) rememberServerConfiguredFeed(snapshot);

    if (snapshot) {
      const snapshotFeedQuery = normalizedFeed
        ? `?feed=${encodeURIComponent(normalizedFeed)}`
        : "";
      storeResponse(
        `/market-data/stocks/${encodeURIComponent(normalizedSymbol)}/snapshot${snapshotFeedQuery}`,
        snapshot,
        SNAPSHOT_CACHE_TTL_MS,
      );
    }

    if (payload?.bars) {
      const returnedBarsFeed = String(payload?.bars?.source?.feed || "")
        .trim()
        .toLowerCase();
      const cacheBarsFeed = returnedBarsFeed || normalizedBarsFeed || normalizedFeed;
      storeResponse(
        stockBarsPath(normalizedSymbol, normalizedTimeframe, cacheBarsFeed, limit),
        payload.bars,
        BARS_CACHE_TTL_MS,
      );
    }

    if (payload?.delayed_close_bars) {
      storeResponse(
        stockBarsPath(normalizedSymbol, "1D", "delayed_sip", 20),
        payload.delayed_close_bars,
        BARS_CACHE_TTL_MS,
      );
    }

    return payload;
  });

  liveBundleInFlightRequests.set(normalizedSymbol, request);

  return request.finally(() => {
    if (liveBundleInFlightRequests.get(normalizedSymbol) === request) {
      liveBundleInFlightRequests.delete(normalizedSymbol);
    }
  });
}

export function getStockInitialLiveData(symbol, options = {}) {
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();

  liveBundleInitializedSymbols.add(normalizedSymbol);
  return getStockSnapshot(normalizedSymbol, undefined, {
    cacheMode: "reload",
    publicMarketData: options.publicMarketData === true,
  }).then((snapshot) => ({ snapshot, bundledBars: null }));
}

export function getStockSnapshot(symbol, feed, options = {}) {
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();
  const normalizedFeed = feed ? String(feed).trim().toLowerCase() : "";
  const marketDataBasePath = options.publicMarketData === true ? "/public/market-data" : "/market-data";
  const feedQuery = normalizedFeed
    ? `?feed=${encodeURIComponent(normalizedFeed)}`
    : "";

  const readSnapshot = () => {
    const request = readMarketData(
      `${marketDataBasePath}/stocks/${encodeURIComponent(normalizedSymbol)}/snapshot${feedQuery}`,
      {
        ...options,
        cacheTtlMs: options.cacheTtlMs ?? SNAPSHOT_CACHE_TTL_MS,
      },
    );

    // This remains the safe fallback path if the initial bundle cannot complete.
    if (!normalizedFeed && options.cacheMode !== "reload") {
      warmSelectedSymbolBars(normalizedSymbol);
    }

    return request.then((payload) => {
      if (!normalizedFeed) rememberServerConfiguredFeed(payload);
      return payload;
    });
  };

  const inFlightLiveBundle =
    !normalizedFeed && options.cacheMode !== "reload"
      ? liveBundleInFlightRequests.get(normalizedSymbol)
      : null;

  if (inFlightLiveBundle) {
    return inFlightLiveBundle.then(
      (payload) => {
        if (!payload?.snapshot) return readSnapshot();
        rememberServerConfiguredFeed(payload.snapshot);
        return payload.snapshot;
      },
      () => readSnapshot(),
    );
  }

  const shouldUseInitialLiveBundle =
    CACHE_ENABLED &&
    !normalizedTestEnvironment() &&
    !normalizedFeed &&
    options.cacheMode !== "reload" &&
    normalizedSymbol === selectedLiveSymbol() &&
    !liveBundleInitializedSymbols.has(normalizedSymbol);

  if (!shouldUseInitialLiveBundle) return readSnapshot();

  liveBundleInitializedSymbols.add(normalizedSymbol);
  return getStockLiveBundle(
    normalizedSymbol,
    "5m",
    undefined,
    LIVE_DEFAULT_BAR_WARM_LIMIT,
    { includeDelayedClose: false },
  )
    .then((payload) => {
      if (!payload?.snapshot) {
        throw new Error("Authenticated live market snapshot is unavailable.");
      }
      return payload.snapshot;
    })
    .catch(() => {
      liveBundleInitializedSymbols.delete(normalizedSymbol);
      return readSnapshot();
    });
}
export function getStockNews(symbol, limit = 6, options = {}) {
  const normalizedSymbol = String(symbol || "")
    .trim()
    .toUpperCase();

  const normalizedLimit = Math.max(
    1,
    Math.min(Number(limit) || 6, 10),
  );

  const params = new URLSearchParams({
    limit: String(normalizedLimit),
  });

  return readMarketData(
    `/market-data/stocks/${encodeURIComponent(
      normalizedSymbol,
    )}/news?${params.toString()}`,
    options,
  );
}

export function getStockCorporateActions(symbol, options = {}) {
  const normalizedSymbol = String(symbol || "")
    .trim()
    .toUpperCase();

  const params = new URLSearchParams({
    days_back: "90",
    days_forward: "180",
    limit: "100",
  });

  return readMarketData(
    `/market-data/stocks/${encodeURIComponent(
      normalizedSymbol,
    )}/corporate-actions?${params.toString()}`,
    options,
  );
}

export function getStockReportedQuarter(symbol, options = {}) {
  const normalizedSymbol = String(symbol || "")
    .trim()
    .toUpperCase();

  return readMarketData(
    `/market-data/stocks/${encodeURIComponent(
      normalizedSymbol,
    )}/reported-quarter`,
    options,
  );
}

export function getMarketNews(limit = 6, options = {}) {
  const normalizedLimit = Math.max(
    1,
    Math.min(Number(limit) || 6, 10),
  );

  const params = new URLSearchParams({
    limit: String(normalizedLimit),
  });

  return readMarketData(
    `/market-data/news?${params.toString()}`,
    options,
  );
}
export function getStockMarketMovers(top = 10, options = {}) {
  const normalizedTop = Math.max(
    1,
    Math.min(Math.trunc(Number(top) || 10), 50),
  );
  const params = new URLSearchParams({
    top: String(normalizedTop),
  });

  return readMarketData(
    `/market-data/stocks/movers?${params.toString()}`,
    options,
  );
}


export function getStockMostActives(top = 100, options = {}) {
  const normalizedTop = Math.max(
    1,
    Math.min(Math.trunc(Number(top) || 100), 100),
  );
  const params = new URLSearchParams({
    top: String(normalizedTop),
  });

  return readMarketData(
    `/market-data/stocks/most-actives?${params.toString()}`,
    options,
  );
}

export function getOptionChainSnapshots(
  symbol,
  {
    feed = "indicative",
    limit = 100,
    contractType,
    strikePriceGte,
    strikePriceLte,
    expirationDateGte,
    expirationDateLte,
    pageToken,
  } = {},
  options = {},
) {
  const normalizedSymbol = String(symbol || "")
    .trim()
    .toUpperCase();
  const requestedFeed = String(feed || "indicative")
    .trim()
    .toLowerCase();
  const normalizedFeed = ["indicative", "opra"].includes(requestedFeed)
    ? requestedFeed
    : "indicative";
  const normalizedLimit = Math.max(
    1,
    Math.min(Math.trunc(Number(limit) || 100), 1000),
  );
  const normalizedContractType = String(contractType || "")
    .trim()
    .toLowerCase();

  const params = new URLSearchParams({
    feed: normalizedFeed,
    limit: String(normalizedLimit),
  });
  if (["call", "put"].includes(normalizedContractType)) {
    params.set("contract_type", normalizedContractType);
  }
  if (Number.isFinite(Number(strikePriceGte)) && Number(strikePriceGte) > 0) {
    params.set("strike_price_gte", String(Number(strikePriceGte)));
  }
  if (Number.isFinite(Number(strikePriceLte)) && Number(strikePriceLte) > 0) {
    params.set("strike_price_lte", String(Number(strikePriceLte)));
  }
  if (expirationDateGte) {
    params.set("expiration_date_gte", String(expirationDateGte).trim());
  }
  if (expirationDateLte) {
    params.set("expiration_date_lte", String(expirationDateLte).trim());
  }
  if (pageToken) {
    params.set("page_token", String(pageToken).trim());
  }

  return readMarketData(
    `/market-data/options/chain/${encodeURIComponent(normalizedSymbol)}?${params.toString()}`,
    options,
  );
}


export function getStockSnapshots(symbols, feed, options = {}) {
  const normalizedSymbols = normalizeBatchSymbols(symbols);
  const normalizedFeed = feed ? String(feed).trim().toLowerCase() : "";
  const params = new URLSearchParams({
    symbols: normalizedSymbols.join(","),
  });
  if (normalizedFeed) params.set("feed", normalizedFeed);

  return readMarketData(
    `/market-data/stocks/snapshots?${params.toString()}`,
    {
      ...options,
      cacheTtlMs: options.cacheTtlMs ?? SNAPSHOT_CACHE_TTL_MS,
    },
  ).then((payload) => {
    if (!normalizedFeed) rememberServerConfiguredFeed(payload);
    return payload;
  });
}
export function getOvernightStockBars(symbols, options = {}) {
  const normalizedSymbols = normalizeBatchSymbols(symbols);
  const params = new URLSearchParams({
    symbols: normalizedSymbols.join(","),
  });

  return readMarketData(
    `/market-data/stocks/overnight-bars?${params.toString()}`,
    {
      ...options,
      cacheTtlMs: options.cacheTtlMs ?? BARS_CACHE_TTL_MS,
    },
  );
}
export function getStockBars(symbol, timeframe = "5m", feed, limit = 160, options = {}) {
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();
  const normalizedTimeframe = String(timeframe || "5m").trim();
  const normalizedFeed = feed ? String(feed).trim().toLowerCase() : "";
  const normalizedLimit = Number(limit);

  const readBars = () =>
    readMarketData(stockBarsPath(normalizedSymbol, normalizedTimeframe, normalizedFeed, limit, options), {
      ...options,
      cacheTtlMs: options.cacheTtlMs ?? BARS_CACHE_TTL_MS,
    });

  const inFlightLiveBundle =
    options.cacheMode !== "reload"
      ? liveBundleInFlightRequests.get(normalizedSymbol)
      : null;
  const requestsBundledChartBars =
    normalizedTimeframe === "5m" && normalizedLimit === LIVE_DEFAULT_BAR_WARM_LIMIT;
  const requestsBundledDelayedClose =
    normalizedTimeframe === "1D" &&
    normalizedFeed === "delayed_sip" &&
    normalizedLimit === 20;

  if (
    inFlightLiveBundle &&
    (requestsBundledChartBars || requestsBundledDelayedClose)
  ) {
    return inFlightLiveBundle.then(
      (payload) => {
        if (requestsBundledDelayedClose && payload?.delayed_close_bars) {
          return payload.delayed_close_bars;
        }

        if (requestsBundledChartBars && payload?.bars) {
          const bundleFeed = String(payload?.bars?.source?.feed || "")
            .trim()
            .toLowerCase();
          if (!normalizedFeed || normalizedFeed === bundleFeed) {
            return payload.bars;
          }
        }

        return readBars();
      },
      () => readBars(),
    );
  }

  return readBars();
}
